import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const positions = new Map<string, number>();

/**
 * Back/forward returns the scroll pane to where it was for that history entry;
 * a new navigation to another page starts at the top.
 */
export function useScrollRestore(ref: React.RefObject<HTMLElement | null>) {
	const location = useLocation();
	const type = useNavigationType();
	const lastPath = useRef(location.pathname);
	const keyRef = useRef(location.key);

	// Remember the position for the current entry as the pane scrolls.
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const onScroll = () => positions.set(keyRef.current, el.scrollTop);
		el.addEventListener("scroll", onScroll, { passive: true });
		return () => el.removeEventListener("scroll", onScroll);
	}, [ref]);

	useLayoutEffect(() => {
		const el = ref.current;
		keyRef.current = location.key;
		const pathChanged = lastPath.current !== location.pathname;
		lastPath.current = location.pathname;
		if (!el) return;
		if (type !== "POP") {
			if (pathChanged) el.scrollTo(0, 0);
			return;
		}
		const target = positions.get(location.key) ?? 0;
		// Content can arrive a moment later (simulation results), so retry briefly until the page is tall enough.
		let frame = 0;
		let tries = 0;
		const attempt = () => {
			el.scrollTo(0, target);
			if (Math.abs(el.scrollTop - target) > 2 && tries++ < 40) frame = requestAnimationFrame(attempt);
		};
		attempt();
		const stop = () => cancelAnimationFrame(frame);
		el.addEventListener("wheel", stop, { once: true, passive: true });
		el.addEventListener("touchstart", stop, { once: true, passive: true });
		return () => {
			stop();
			el.removeEventListener("wheel", stop);
			el.removeEventListener("touchstart", stop);
		};
	}, [ref, location.key, location.pathname, type]);
}
